// ---------------------------------------------------------------------------
// Props — Static prop system with procedural mesh generators, instanced
// rendering via InstancedMesh, and distance-based LOD.
// ---------------------------------------------------------------------------

import * as THREE from 'three';
import type { PropDef } from './map-format';
import type { MaterialFactory } from './map-loader';
import { addBoxCollider } from '../gameplay/collision';
import { getPropBatcher, disposePropBatcher, type BatchableProp } from './prop-batching';
import { getPropLODSystem, disposePropLODSystem } from './prop-lod';
import { settingsStore } from '../core/settings';
import { getPropModelLoader, disposePropModelLoader } from './prop-model-loader';
import { MAP_PROP_MODELS, type MapPropModelConfig } from './map-prop-models';

// ---------------------------------------------------------------------------
// Distance-based GLB management — lazy load/unload based on camera proximity
// ---------------------------------------------------------------------------

/** Track placed props with their GLB state for distance-based management. */
interface ManagedProp {
  propDef: PropDef;
  /**
   * Resolved prop-type key (post-`sanitizePropType`) used for registry /
   * MAP_PROP_MODELS / propModelLoader lookups. May differ from
   * `propDef.type` when a Blender duplicate-suffix has been stripped.
   */
  resolvedType: string;
  /** The procedural mesh (always available as fallback). */
  proceduralMesh: THREE.Mesh | THREE.Group;
  /** The loaded GLB model, if currently in scene. */
  glbModel: THREE.Group | null;
  /** World position for distance checks. */
  position: THREE.Vector3;
  /** Whether the GLB model is currently loaded and visible. */
  isGLBLoaded: boolean;
  /** Whether an async GLB load is currently in flight. */
  isLoading: boolean;
  /** The GLB config from the manifest. */
  glbConfig: MapPropModelConfig;
  /** The scene this prop belongs to (for add/remove). */
  scene: THREE.Scene;
  /** Collider size for LOD wrapping. */
  colliderSize: [number, number, number] | null;
}

const managedProps: ManagedProp[] = [];

/** Distance (metres) within which GLB models start loading. */
const GLB_LOAD_DISTANCE = 50;
/** Distance (metres) beyond which GLB models are unloaded. Uses hysteresis to prevent thrashing. */
const GLB_UNLOAD_DISTANCE = 100;

// ---------------------------------------------------------------------------
// Prop registry — maps prop type names to mesh generator functions
// ---------------------------------------------------------------------------

interface PropTemplate {
  /**
   * Generate the prop mesh or group.
   * Multi-part props return a THREE.Group; simple single-geometry props
   * return a THREE.Mesh. Call sites must narrow with `instanceof`.
   */
  generate: (matFactory: MaterialFactory) => THREE.Mesh | THREE.Group;
  /** Collision AABB size [w, h, d] relative to prop origin. null = no collision. */
  colliderSize: [number, number, number] | null;
  /** LOD distances: [full, simplified, billboard]. */
  lodDistances?: [number, number, number];
}

const propRegistry = new Map<string, PropTemplate>();

/**
 * Sanitize a raw prop-type string before registry/MAP_PROP_MODELS lookup.
 * F-MAP-009: GLB markers from Blender often pick up `_001`, `_002` numeric
 * suffixes when an object is duplicated. Strip a single trailing `_<digits>`
 * group so `crate_LARGE_001` resolves to `crate_LARGE` (and on to `crate` if
 * the more-specific entry is also missing). The two-step lookup at
 * `loadProps` keeps the original behaviour for already-correct names.
 */
export function sanitizePropType(rawType: string): string {
  return rawType.replace(/_\d+$/, '');
}

// Helper to create materials with consistent PBR settings
function propMat(color: number, roughness = 0.7, metalness = 0): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, roughness, metalness });
}

// ---------------------------------------------------------------------------
// Prop type definitions
// ---------------------------------------------------------------------------

// ---- Crate (wooden box) ----
propRegistry.set('crate', {
  generate: () => {
    const group = new THREE.Group();
    const bodyGeo = new THREE.BoxGeometry(1.2, 1.2, 1.2);
    const bodyMat = propMat(0x8b6f47, 0.85);
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    body.castShadow = true;
    body.receiveShadow = true;

    // Cross braces on top
    const braceGeo = new THREE.BoxGeometry(1.25, 0.05, 0.08);
    const braceMat = propMat(0x6a5535, 0.9);
    const brace1 = new THREE.Mesh(braceGeo, braceMat);
    brace1.position.y = 0.63;
    brace1.rotation.y = Math.PI / 4;
    const brace2 = new THREE.Mesh(braceGeo.clone(), braceMat.clone());
    brace2.position.y = 0.63;
    brace2.rotation.y = -Math.PI / 4;

    // Metal corner reinforcements
    const cornerGeo = new THREE.BoxGeometry(0.08, 1.24, 0.08);
    const cornerMat = propMat(0x555555, 0.4, 0.6);
    const corners = [
      [-0.56, 0, -0.56], [0.56, 0, -0.56],
      [-0.56, 0, 0.56], [0.56, 0, 0.56],
    ];
    for (const [x, y, z] of corners) {
      const corner = new THREE.Mesh(cornerGeo, cornerMat);
      corner.position.set(x, y, z);
      group.add(corner);
    }

    group.add(body, brace1, brace2);
    return group;
  },
  colliderSize: [1.2, 1.2, 1.2],
});

// ---- Crate Small ----
propRegistry.set('crate_small', {
  generate: () => {
    const geo = new THREE.BoxGeometry(0.7, 0.5, 0.7);
    const mat = propMat(0x9a7f57, 0.85);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  },
  colliderSize: [0.7, 0.5, 0.7],
});

// ---- Barrel ----
propRegistry.set('barrel', {
  generate: () => {
    const group = new THREE.Group();
    const bodyGeo = new THREE.CylinderGeometry(0.4, 0.4, 1.0, 12);
    const bodyMat = propMat(0x4a6a3a, 0.7);
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    body.castShadow = true;
    body.receiveShadow = true;

    // Metal bands
    const bandGeo = new THREE.TorusGeometry(0.41, 0.02, 4, 12);
    const bandMat = propMat(0x666666, 0.3, 0.7);
    const band1 = new THREE.Mesh(bandGeo, bandMat);
    band1.rotation.x = Math.PI / 2;
    band1.position.y = 0.35;
    const band2 = new THREE.Mesh(bandGeo.clone(), bandMat.clone());
    band2.rotation.x = Math.PI / 2;
    band2.position.y = -0.35;

    group.add(body, band1, band2);
    return group;
  },
  colliderSize: [0.8, 1.0, 0.8],
});

// ---- Barrel Rusty ----
propRegistry.set('barrel_rusty', {
  generate: () => {
    const group = new THREE.Group();
    const bodyGeo = new THREE.CylinderGeometry(0.4, 0.38, 1.0, 12);
    const bodyMat = propMat(0x8b4030, 0.8, 0.2);
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    body.castShadow = true;
    body.receiveShadow = true;

    const bandGeo = new THREE.TorusGeometry(0.41, 0.02, 4, 12);
    const bandMat = propMat(0x554433, 0.5, 0.4);
    const band1 = new THREE.Mesh(bandGeo, bandMat);
    band1.rotation.x = Math.PI / 2;
    band1.position.y = 0.35;

    group.add(body, band1);
    return group;
  },
  colliderSize: [0.8, 1.0, 0.8],
});

// ---- Dumpster ----
propRegistry.set('dumpster', {
  generate: () => {
    const group = new THREE.Group();
    const bodyGeo = new THREE.BoxGeometry(2.0, 1.2, 1.2);
    const bodyMat = propMat(0x3a5a3a, 0.7);
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    body.position.y = 0.2;
    body.castShadow = true;
    body.receiveShadow = true;

    // Lid (slightly tilted)
    const lidGeo = new THREE.BoxGeometry(2.0, 0.06, 1.2);
    const lidMat = propMat(0x2a4a2a, 0.7);
    const lid = new THREE.Mesh(lidGeo, lidMat);
    lid.position.set(0, 0.83, 0);
    lid.rotation.x = -0.1;
    lid.castShadow = true;

    // Wheels
    const wheelGeo = new THREE.CylinderGeometry(0.12, 0.12, 0.06, 8);
    const wheelMat = propMat(0x222222, 0.9);
    const wheelPositions = [[-0.7, -0.4, 0.6], [0.7, -0.4, 0.6]];
    for (const [x, y, z] of wheelPositions) {
      const wheel = new THREE.Mesh(wheelGeo, wheelMat);
      wheel.position.set(x, y, z);
      wheel.rotation.x = Math.PI / 2;
      group.add(wheel);
    }

    group.add(body, lid);
    return group;
  },
  colliderSize: [2.0, 1.4, 1.2],
});

// ---- Sandbag Stack ----
propRegistry.set('sandbags', {
  generate: () => {
    const group = new THREE.Group();
    const bagGeo = new THREE.BoxGeometry(0.8, 0.25, 0.4);
    const bagMat = propMat(0xb8a070, 0.95);

    // 3 rows of sandbags, staggered
    const positions = [
      // Bottom row
      [0, 0.125, 0], [0.85, 0.125, 0], [-0.85, 0.125, 0],
      // Middle row (offset)
      [0.42, 0.375, 0], [-0.42, 0.375, 0],
      // Top row
      [0, 0.625, 0],
    ];
    // Deterministic per-bag yaw offsets so repeat loads render identically.
    // Matches the ±0.05 rad jitter the original Math.random() produced.
    const yawOffsets = [0.031, -0.042, 0.017, -0.028, 0.046, -0.011];
    for (let i = 0; i < positions.length; i++) {
      const [x, y, z] = positions[i];
      const bag = new THREE.Mesh(bagGeo, bagMat);
      bag.position.set(x, y, z);
      bag.rotation.y = yawOffsets[i];
      bag.castShadow = true;
      bag.receiveShadow = true;
      group.add(bag);
    }

    return group;
  },
  colliderSize: [2.5, 0.75, 0.4],
});

// ---- Car (simplified sedan) ----
propRegistry.set('car', {
  generate: () => {
    const group = new THREE.Group();

    // Body
    const bodyGeo = new THREE.BoxGeometry(4.0, 1.0, 1.8);
    const bodyMat = propMat(0x4a4a5a, 0.6, 0.1);
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    body.position.y = 0.6;
    body.castShadow = true;
    body.receiveShadow = true;

    // Cabin
    const cabinGeo = new THREE.BoxGeometry(2.0, 0.8, 1.6);
    const cabinMat = propMat(0x3a3a4a, 0.5, 0.1);
    const cabin = new THREE.Mesh(cabinGeo, cabinMat);
    cabin.position.set(-0.2, 1.5, 0);

    // Windshield (glass)
    const windshieldGeo = new THREE.BoxGeometry(0.05, 0.7, 1.5);
    const windshieldMat = new THREE.MeshStandardMaterial({
      color: 0x88aacc, roughness: 0.05, metalness: 0.9,
      transparent: true, opacity: 0.4,
    });
    const windshield = new THREE.Mesh(windshieldGeo, windshieldMat);
    windshield.position.set(0.82, 1.45, 0);
    windshield.rotation.z = 0.3;

    // Wheels
    const wheelGeo = new THREE.CylinderGeometry(0.3, 0.3, 0.2, 8);
    const wheelMat = propMat(0x1a1a1a, 0.9);
    const wheelPos = [
      [1.3, 0.3, 1.0], [1.3, 0.3, -1.0],
      [-1.3, 0.3, 1.0], [-1.3, 0.3, -1.0],
    ];
    for (const [x, y, z] of wheelPos) {
      const wheel = new THREE.Mesh(wheelGeo, wheelMat);
      wheel.position.set(x, y, z);
      wheel.rotation.x = Math.PI / 2;
      group.add(wheel);
    }

    group.add(body, cabin, windshield);
    return group;
  },
  colliderSize: [4.0, 2.0, 1.8],
});

// ---- Street Sign ----
propRegistry.set('sign', {
  generate: () => {
    const group = new THREE.Group();

    // Pole
    const poleGeo = new THREE.CylinderGeometry(0.04, 0.04, 2.5, 6);
    const poleMat = propMat(0x777777, 0.3, 0.7);
    const pole = new THREE.Mesh(poleGeo, poleMat);
    pole.position.y = 1.25;
    pole.castShadow = true;

    // Sign plate
    const plateGeo = new THREE.BoxGeometry(0.8, 0.5, 0.03);
    const plateMat = propMat(0x2255aa, 0.5);
    const plate = new THREE.Mesh(plateGeo, plateMat);
    plate.position.y = 2.3;
    plate.castShadow = true;

    group.add(pole, plate);
    return group;
  },
  colliderSize: [0.1, 2.5, 0.1],
});

// ---- Light Fixture (wall-mounted) ----
propRegistry.set('light_fixture', {
  generate: () => {
    const group = new THREE.Group();

    // Bracket
    const bracketGeo = new THREE.BoxGeometry(0.15, 0.15, 0.3);
    const bracketMat = propMat(0x555555, 0.3, 0.6);
    const bracket = new THREE.Mesh(bracketGeo, bracketMat);

    // Shade
    const shadeGeo = new THREE.CylinderGeometry(0.12, 0.2, 0.2, 8);
    const shadeMat = propMat(0x333333, 0.4, 0.5);
    const shade = new THREE.Mesh(shadeGeo, shadeMat);
    shade.position.set(0, -0.1, 0.2);

    // Glow bulb (emissive)
    const bulbGeo = new THREE.SphereGeometry(0.06, 6, 6);
    const bulbMat = new THREE.MeshStandardMaterial({
      color: 0xffcc66, emissive: 0xffcc66, emissiveIntensity: 0.8,
    });
    const bulb = new THREE.Mesh(bulbGeo, bulbMat);
    bulb.position.set(0, -0.05, 0.2);

    group.add(bracket, shade, bulb);
    return group;
  },
  colliderSize: null,
});

// ---- AC Unit ----
propRegistry.set('ac_unit', {
  generate: () => {
    const group = new THREE.Group();

    const boxGeo = new THREE.BoxGeometry(0.8, 0.5, 0.4);
    const boxMat = propMat(0xcccccc, 0.7, 0.1);
    const box = new THREE.Mesh(boxGeo, boxMat);
    box.castShadow = true;
    box.receiveShadow = true;

    // Vent grille lines
    const ventGeo = new THREE.BoxGeometry(0.7, 0.02, 0.01);
    const ventMat = propMat(0x999999, 0.4, 0.3);
    for (let i = 0; i < 6; i++) {
      const vent = new THREE.Mesh(ventGeo, ventMat);
      vent.position.set(0, -0.15 + i * 0.06, 0.21);
      group.add(vent);
    }

    group.add(box);
    return group;
  },
  colliderSize: [0.8, 0.5, 0.4],
});

// ---- Pipe (horizontal) ----
propRegistry.set('pipe', {
  generate: () => {
    const geo = new THREE.CylinderGeometry(0.06, 0.06, 3.0, 6);
    const mat = propMat(0x888888, 0.3, 0.6);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.z = Math.PI / 2; // Horizontal
    mesh.castShadow = true;
    return mesh;
  },
  colliderSize: null,
});

// ---- Concrete Barrier ----
propRegistry.set('barrier', {
  generate: () => {
    const group = new THREE.Group();

    // Trapezoidal shape: wider at base
    const shape = new THREE.Shape();
    shape.moveTo(-0.4, 0);
    shape.lineTo(0.4, 0);
    shape.lineTo(0.3, 0.8);
    shape.lineTo(-0.3, 0.8);
    shape.lineTo(-0.4, 0);

    const extrudeSettings: THREE.ExtrudeGeometryOptions = {
      steps: 1, depth: 1.5, bevelEnabled: false,
    };
    const geo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geo.translate(0, 0, -0.75); // Center on Z
    const mat = propMat(0xa09888, 0.85);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    group.add(mesh);
    return group;
  },
  colliderSize: [0.8, 0.8, 1.5],
});

// ---- Tire Stack ----
propRegistry.set('tires', {
  generate: () => {
    const group = new THREE.Group();
    const tireGeo = new THREE.TorusGeometry(0.3, 0.12, 8, 12);
    const tireMat = propMat(0x1a1a1a, 0.95);

    const positions = [
      [0, 0.12, 0], [0.25, 0.12, 0.3], [-0.2, 0.12, 0.25],
      [0.1, 0.36, 0.1],
    ];
    for (const [x, y, z] of positions) {
      const tire = new THREE.Mesh(tireGeo, tireMat);
      tire.position.set(x, y, z);
      tire.rotation.x = Math.PI / 2;
      tire.rotation.z = (Math.random() - 0.5) * 0.3;
      tire.castShadow = true;
      group.add(tire);
    }

    return group;
  },
  colliderSize: [0.8, 0.5, 0.8],
});

// ---- Pallet ----
propRegistry.set('pallet', {
  generate: () => {
    const group = new THREE.Group();
    const boardMat = propMat(0xa07840, 0.9);
    const ledgerMat = propMat(0x8a6535, 0.9);

    // Top deck boards (5 slats along length)
    const slat1Geo = new THREE.BoxGeometry(1.2, 0.04, 0.1);
    for (let i = 0; i < 5; i++) {
      const slat = new THREE.Mesh(slat1Geo, boardMat);
      slat.position.set(0, 0.1, -0.35 + i * 0.175);
      slat.castShadow = true;
      slat.receiveShadow = true;
      group.add(slat);
    }

    // Bottom support runners (3 along depth)
    const runnerGeo = new THREE.BoxGeometry(0.1, 0.08, 0.8);
    for (let i = 0; i < 3; i++) {
      const runner = new THREE.Mesh(runnerGeo, ledgerMat);
      runner.position.set(-0.5 + i * 0.5, 0.04, 0);
      runner.castShadow = true;
      runner.receiveShadow = true;
      group.add(runner);
    }

    return group;
  },
  colliderSize: [1.2, 0.15, 0.8],
});

// ---- Chain Fence ----
propRegistry.set('chain_fence', {
  generate: () => {
    const group = new THREE.Group();
    const frameMat = propMat(0x888888, 0.4, 0.5);

    // Top and bottom rails
    const railGeo = new THREE.BoxGeometry(2.0, 0.04, 0.04);
    const topRail = new THREE.Mesh(railGeo, frameMat);
    topRail.position.y = 1.0;
    topRail.castShadow = true;
    const botRail = new THREE.Mesh(railGeo.clone(), frameMat.clone());
    botRail.position.y = 0.0;

    // Vertical posts
    const postGeo = new THREE.BoxGeometry(0.04, 2.0, 0.04);
    const leftPost = new THREE.Mesh(postGeo, frameMat);
    leftPost.position.set(-0.98, 1.0, 0);
    leftPost.castShadow = true;
    const rightPost = new THREE.Mesh(postGeo.clone(), frameMat.clone());
    rightPost.position.set(0.98, 1.0, 0);
    rightPost.castShadow = true;

    // Diamond mesh pattern (simplified as thin horizontal + diagonal strips)
    const wireMat = propMat(0x999999, 0.4, 0.4);
    const hWireGeo = new THREE.BoxGeometry(2.0, 0.02, 0.02);
    for (let i = 0; i < 8; i++) {
      const wire = new THREE.Mesh(hWireGeo, wireMat);
      wire.position.y = 0.12 + i * 0.11;
      group.add(wire);
    }

    group.add(topRail, botRail, leftPost, rightPost);
    return group;
  },
  colliderSize: [2.0, 2.0, 0.05],
});

// ---- Scaffolding ----
propRegistry.set('scaffolding', {
  generate: () => {
    const group = new THREE.Group();
    const tubeMat = propMat(0x778888, 0.4, 0.5);

    // Vertical uprights (4 corners)
    const uprightGeo = new THREE.CylinderGeometry(0.04, 0.04, 3.0, 6);
    const uprightPos = [[-0.9, 1.5, -0.65], [0.9, 1.5, -0.65], [-0.9, 1.5, 0.65], [0.9, 1.5, 0.65]];
    for (const [x, y, z] of uprightPos) {
      const u = new THREE.Mesh(uprightGeo, tubeMat);
      u.position.set(x, y, z);
      u.castShadow = true;
      group.add(u);
    }

    // Horizontal crossbars (two levels)
    const hBarGeo = new THREE.CylinderGeometry(0.03, 0.03, 1.8, 6);
    hBarGeo.rotateZ(Math.PI / 2);
    for (const y of [1.0, 2.5]) {
      for (const z of [-0.65, 0.65]) {
        const bar = new THREE.Mesh(hBarGeo, tubeMat);
        bar.position.set(0, y, z);
        bar.castShadow = true;
        group.add(bar);
      }
    }

    // Platform planks at mid height
    const plankGeo = new THREE.BoxGeometry(1.8, 0.05, 1.3);
    const plankMat = propMat(0xa07840, 0.9);
    const platform = new THREE.Mesh(plankGeo, plankMat);
    platform.position.y = 1.52;
    platform.castShadow = true;
    platform.receiveShadow = true;
    group.add(platform);

    return group;
  },
  colliderSize: [2.0, 3.0, 1.5],
});

// ---- Trash Bag ----
propRegistry.set('trash_bag', {
  generate: () => {
    const group = new THREE.Group();
    const bagMat = propMat(0x1a1a1a, 0.95);

    // Main lumpy body — multiple overlapping spheres
    const mainGeo = new THREE.SphereGeometry(0.3, 8, 6);
    const main = new THREE.Mesh(mainGeo, bagMat);
    main.scale.set(1.0, 1.2, 0.9);
    main.position.y = 0.3;
    main.castShadow = true;
    main.receiveShadow = true;

    const lump1Geo = new THREE.SphereGeometry(0.2, 7, 5);
    const lump1 = new THREE.Mesh(lump1Geo, bagMat);
    lump1.position.set(0.15, 0.45, 0.1);
    lump1.scale.set(1.1, 0.9, 1.0);
    lump1.castShadow = true;

    const lump2Geo = new THREE.SphereGeometry(0.18, 7, 5);
    const lump2 = new THREE.Mesh(lump2Geo, bagMat);
    lump2.position.set(-0.12, 0.4, -0.1);
    lump2.scale.set(0.9, 1.1, 1.0);
    lump2.castShadow = true;

    // Twist tie at top
    const tieGeo = new THREE.TorusGeometry(0.06, 0.015, 4, 8);
    const tieMat = propMat(0x2a2a2a, 0.8);
    const tie = new THREE.Mesh(tieGeo, tieMat);
    tie.position.y = 0.58;
    tie.rotation.x = Math.PI / 2;

    group.add(main, lump1, lump2, tie);
    return group;
  },
  colliderSize: [0.5, 0.6, 0.5],
});

// ---- Ammo Crate ----
propRegistry.set('ammo_crate', {
  generate: () => {
    const group = new THREE.Group();
    const bodyMat = propMat(0x4a5a3a, 0.8);
    const metalMat = propMat(0x666655, 0.4, 0.4);

    const bodyGeo = new THREE.BoxGeometry(0.8, 0.5, 0.6);
    const body = new THREE.Mesh(bodyGeo, bodyMat);
    body.castShadow = true;
    body.receiveShadow = true;

    // Lid (slightly raised)
    const lidGeo = new THREE.BoxGeometry(0.82, 0.06, 0.62);
    const lid = new THREE.Mesh(lidGeo, bodyMat);
    lid.position.y = 0.28;
    lid.castShadow = true;

    // Cross braces on top face
    const braceGeo = new THREE.BoxGeometry(0.78, 0.015, 0.04);
    const brace1 = new THREE.Mesh(braceGeo, metalMat);
    brace1.position.y = 0.32;
    const brace2 = new THREE.Mesh(braceGeo.clone(), metalMat.clone());
    brace2.position.y = 0.32;
    brace2.rotation.y = Math.PI / 2;
    brace2.scale.x = 0.75;

    // Clasp
    const claspGeo = new THREE.BoxGeometry(0.1, 0.06, 0.03);
    const clasp = new THREE.Mesh(claspGeo, metalMat);
    clasp.position.set(0, 0.25, 0.32);

    group.add(body, lid, brace1, brace2, clasp);
    return group;
  },
  colliderSize: [0.8, 0.5, 0.6],
});

// ---- Fire Hydrant ----
propRegistry.set('fire_hydrant', {
  generate: () => {
    const group = new THREE.Group();
    const redMat = propMat(0xcc2222, 0.5, 0.1);
    const silvMat = propMat(0xaaaaaa, 0.3, 0.6);

    // Base
    const baseGeo = new THREE.CylinderGeometry(0.18, 0.2, 0.12, 8);
    const base = new THREE.Mesh(baseGeo, redMat);
    base.position.y = 0.06;
    base.castShadow = true;
    base.receiveShadow = true;

    // Body
    const bodyGeo = new THREE.CylinderGeometry(0.15, 0.17, 0.42, 8);
    const body = new THREE.Mesh(bodyGeo, redMat);
    body.position.y = 0.33;
    body.castShadow = true;

    // Cap dome
    const capGeo = new THREE.CylinderGeometry(0.06, 0.15, 0.16, 8);
    const cap = new THREE.Mesh(capGeo, redMat);
    cap.position.y = 0.62;
    cap.castShadow = true;

    // Side outlet nozzles (2)
    const nozzleGeo = new THREE.CylinderGeometry(0.04, 0.04, 0.12, 6);
    nozzleGeo.rotateZ(Math.PI / 2);
    for (const xOffset of [-1, 1]) {
      const nozzle = new THREE.Mesh(nozzleGeo, silvMat);
      nozzle.position.set(xOffset * 0.2, 0.3, 0);
      group.add(nozzle);
    }

    group.add(base, body, cap);
    return group;
  },
  colliderSize: [0.3, 0.7, 0.3],
});

// ---- Electrical Box ----
propRegistry.set('electrical_box', {
  generate: () => {
    const group = new THREE.Group();
    const boxMat = propMat(0x888888, 0.5, 0.2);
    const darkMat = propMat(0x444444, 0.6, 0.2);

    const bodyGeo = new THREE.BoxGeometry(0.6, 0.8, 0.2);
    const body = new THREE.Mesh(bodyGeo, boxMat);
    body.castShadow = true;
    body.receiveShadow = true;

    // Conduit entry at bottom
    const conduitGeo = new THREE.CylinderGeometry(0.025, 0.025, 0.15, 6);
    const conduit = new THREE.Mesh(conduitGeo, darkMat);
    conduit.position.set(0.1, -0.475, 0);
    group.add(conduit);

    // Panel door seam
    const seamGeo = new THREE.BoxGeometry(0.55, 0.74, 0.01);
    const seamMat = propMat(0x777777, 0.5, 0.3);
    const seam = new THREE.Mesh(seamGeo, seamMat);
    seam.position.z = 0.105;

    // Latch
    const latchGeo = new THREE.BoxGeometry(0.05, 0.08, 0.04);
    const latch = new THREE.Mesh(latchGeo, darkMat);
    latch.position.set(0.24, 0.0, 0.12);

    group.add(body, seam, latch);
    return group;
  },
  colliderSize: [0.6, 0.8, 0.2],
});

// ---- Bench ----
propRegistry.set('bench', {
  generate: () => {
    const group = new THREE.Group();
    const woodMat = propMat(0x9a7a50, 0.85);
    const metalMat = propMat(0x666666, 0.4, 0.5);

    // Seat slats (3 boards)
    const slatGeo = new THREE.BoxGeometry(1.8, 0.05, 0.14);
    for (let i = 0; i < 3; i++) {
      const slat = new THREE.Mesh(slatGeo, woodMat);
      slat.position.set(0, 0.46, -0.14 + i * 0.14);
      slat.castShadow = true;
      slat.receiveShadow = true;
      group.add(slat);
    }

    // Back rest slats (2 boards)
    const backGeo = new THREE.BoxGeometry(1.8, 0.12, 0.04);
    for (let i = 0; i < 2; i++) {
      const back = new THREE.Mesh(backGeo, woodMat);
      back.position.set(0, 0.62 + i * 0.14, -0.2);
      back.rotation.x = -0.15;
      back.castShadow = true;
      group.add(back);
    }

    // Metal legs (4)
    const legGeo = new THREE.BoxGeometry(0.04, 0.45, 0.04);
    const legPositions = [[-0.8, 0.22, -0.18], [0.8, 0.22, -0.18], [-0.8, 0.22, 0.18], [0.8, 0.22, 0.18]];
    for (const [x, y, z] of legPositions) {
      const leg = new THREE.Mesh(legGeo, metalMat);
      leg.position.set(x, y, z);
      leg.castShadow = true;
      group.add(leg);
    }

    // Foot rail
    const footGeo = new THREE.BoxGeometry(1.7, 0.04, 0.04);
    for (const z of [-0.18, 0.18]) {
      const foot = new THREE.Mesh(footGeo, metalMat);
      foot.position.set(0, 0.02, z);
      group.add(foot);
    }

    return group;
  },
  colliderSize: [1.8, 0.8, 0.5],
});

// ---- Folding Table ----
propRegistry.set('folding_table', {
  generate: () => {
    const group = new THREE.Group();
    const topMat = propMat(0xcccccc, 0.6, 0.1);
    const legMat = propMat(0x888888, 0.4, 0.5);

    // Table top
    const topGeo = new THREE.BoxGeometry(1.5, 0.04, 0.8);
    const top = new THREE.Mesh(topGeo, topMat);
    top.position.y = 0.73;
    top.castShadow = true;
    top.receiveShadow = true;

    // Thin legs (4)
    const legGeo = new THREE.BoxGeometry(0.03, 0.72, 0.03);
    const legPos = [[-0.7, 0.36, -0.37], [0.7, 0.36, -0.37], [-0.7, 0.36, 0.37], [0.7, 0.36, 0.37]];
    for (const [x, y, z] of legPos) {
      const leg = new THREE.Mesh(legGeo, legMat);
      leg.position.set(x, y, z);
      leg.castShadow = true;
      group.add(leg);
    }

    // Crossbar under table
    const xBarGeo = new THREE.BoxGeometry(1.44, 0.02, 0.02);
    for (const z of [-0.3, 0.3]) {
      const bar = new THREE.Mesh(xBarGeo, legMat);
      bar.position.set(0, 0.15, z);
      group.add(bar);
    }

    group.add(top);
    return group;
  },
  colliderSize: [1.5, 0.75, 0.8],
});

// ---- Ladder ----
propRegistry.set('ladder', {
  generate: () => {
    const group = new THREE.Group();
    const metalMat = propMat(0x888888, 0.4, 0.5);

    // Side rails
    const railGeo = new THREE.BoxGeometry(0.04, 3.0, 0.04);
    for (const x of [-0.22, 0.22]) {
      const rail = new THREE.Mesh(railGeo, metalMat);
      rail.position.set(x, 1.5, 0);
      rail.castShadow = true;
      group.add(rail);
    }

    // Rungs (10 evenly spaced)
    const rungGeo = new THREE.BoxGeometry(0.44, 0.03, 0.03);
    for (let i = 0; i < 10; i++) {
      const rung = new THREE.Mesh(rungGeo, metalMat);
      rung.position.y = 0.25 + i * 0.275;
      rung.castShadow = true;
      group.add(rung);
    }

    return group;
  },
  colliderSize: [0.5, 3.0, 0.1],
});

// ---- Cable Spool ----
propRegistry.set('cable_spool', {
  generate: () => {
    const group = new THREE.Group();
    const woodMat = propMat(0xc09050, 0.8);
    const cableMat = propMat(0x333333, 0.7);

    // Core drum
    const drumGeo = new THREE.CylinderGeometry(0.2, 0.2, 0.5, 10);
    const drum = new THREE.Mesh(drumGeo, woodMat);
    drum.position.y = 0.3;
    drum.rotation.x = Math.PI / 2;
    drum.castShadow = true;
    drum.receiveShadow = true;

    // Flanges (2 discs)
    const flangeGeo = new THREE.CylinderGeometry(0.4, 0.4, 0.05, 12);
    for (const z of [-0.27, 0.27]) {
      const flange = new THREE.Mesh(flangeGeo, woodMat);
      flange.position.set(0, 0.3, z);
      flange.rotation.x = Math.PI / 2;
      flange.castShadow = true;
      flange.receiveShadow = true;
      group.add(flange);
    }

    // Wound cable layer
    const cableGeo = new THREE.TorusGeometry(0.25, 0.04, 6, 18);
    const cable = new THREE.Mesh(cableGeo, cableMat);
    cable.position.set(0, 0.3, 0);
    cable.rotation.x = Math.PI / 2;
    group.add(cable);

    group.add(drum);
    return group;
  },
  colliderSize: [0.8, 0.6, 0.8],
});

// ---- Gas Tank ----
propRegistry.set('gas_tank', {
  generate: () => {
    const group = new THREE.Group();
    const tankMat = propMat(0x5a7a5a, 0.5, 0.2);
    const silvMat = propMat(0xaaaaaa, 0.3, 0.6);

    // Main cylinder
    const bodyGeo = new THREE.CylinderGeometry(0.2, 0.2, 0.85, 10);
    const body = new THREE.Mesh(bodyGeo, tankMat);
    body.position.y = 0.5;
    body.castShadow = true;
    body.receiveShadow = true;

    // Top dome
    const topGeo = new THREE.SphereGeometry(0.2, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    const top = new THREE.Mesh(topGeo, tankMat);
    top.position.y = 0.925;
    top.castShadow = true;

    // Bottom dome
    const botGeo = new THREE.SphereGeometry(0.2, 10, 6, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2);
    const bot = new THREE.Mesh(botGeo, tankMat);
    bot.position.y = 0.075;
    bot.receiveShadow = true;

    // Valve on top
    const valveGeo = new THREE.CylinderGeometry(0.04, 0.04, 0.1, 6);
    const valve = new THREE.Mesh(valveGeo, silvMat);
    valve.position.y = 1.1;

    // Collar ring
    const collarGeo = new THREE.TorusGeometry(0.21, 0.02, 4, 12);
    const collar = new THREE.Mesh(collarGeo, silvMat);
    collar.rotation.x = Math.PI / 2;
    collar.position.y = 0.92;

    group.add(body, top, bot, valve, collar);
    return group;
  },
  colliderSize: [0.4, 1.0, 0.4],
});

// ---- Traffic Cone ----
propRegistry.set('traffic_cone', {
  generate: () => {
    const group = new THREE.Group();
    const orangeMat = propMat(0xff6600, 0.7);
    const whiteMat = propMat(0xeeeeee, 0.6);

    // Base
    const baseGeo = new THREE.CylinderGeometry(0.2, 0.22, 0.06, 8);
    const base = new THREE.Mesh(baseGeo, orangeMat);
    base.position.y = 0.03;
    base.castShadow = true;
    base.receiveShadow = true;

    // Cone body (lower orange)
    const lowGeo = new THREE.CylinderGeometry(0.08, 0.2, 0.3, 8);
    const low = new THREE.Mesh(lowGeo, orangeMat);
    low.position.y = 0.21;
    low.castShadow = true;

    // White stripe band
    const stripeGeo = new THREE.CylinderGeometry(0.085, 0.085, 0.06, 8);
    const stripe = new THREE.Mesh(stripeGeo, whiteMat);
    stripe.position.y = 0.39;

    // Cone body (upper orange)
    const upGeo = new THREE.CylinderGeometry(0.02, 0.08, 0.28, 8);
    const up = new THREE.Mesh(upGeo, orangeMat);
    up.position.y = 0.57;
    up.castShadow = true;

    // Tip cap
    const tipGeo = new THREE.CylinderGeometry(0.01, 0.02, 0.04, 6);
    const tip = new THREE.Mesh(tipGeo, orangeMat);
    tip.position.y = 0.73;

    group.add(base, low, stripe, up, tip);
    return group;
  },
  colliderSize: [0.3, 0.7, 0.3],
});

// ---- Cardboard Box ----
propRegistry.set('cardboard_box', {
  generate: () => {
    const group = new THREE.Group();
    const cardMat = propMat(0xc8a060, 0.95);
    const tapeMat = propMat(0xddcc88, 0.7);

    const bodyGeo = new THREE.BoxGeometry(0.5, 0.4, 0.5);
    const body = new THREE.Mesh(bodyGeo, cardMat);
    body.castShadow = true;
    body.receiveShadow = true;

    // Top flap seam
    const seamGeo = new THREE.BoxGeometry(0.52, 0.01, 0.06);
    const seam = new THREE.Mesh(seamGeo, tapeMat);
    seam.position.y = 0.205;

    // Tape cross on top
    const tapeHGeo = new THREE.BoxGeometry(0.5, 0.015, 0.04);
    const tapeH = new THREE.Mesh(tapeHGeo, tapeMat);
    tapeH.position.y = 0.215;

    const tapeVGeo = new THREE.BoxGeometry(0.04, 0.015, 0.5);
    const tapeV = new THREE.Mesh(tapeVGeo, tapeMat);
    tapeV.position.y = 0.215;

    group.add(body, seam, tapeH, tapeV);
    return group;
  },
  colliderSize: [0.5, 0.4, 0.5],
});

// ---- Toolbox ----
propRegistry.set('toolbox', {
  generate: () => {
    const group = new THREE.Group();
    const redMat = propMat(0xcc2222, 0.5, 0.2);
    const metalMat = propMat(0xaaaaaa, 0.3, 0.6);

    const bodyGeo = new THREE.BoxGeometry(0.5, 0.22, 0.3);
    const body = new THREE.Mesh(bodyGeo, redMat);
    body.position.y = 0.11;
    body.castShadow = true;
    body.receiveShadow = true;

    // Lid (top half)
    const lidGeo = new THREE.BoxGeometry(0.5, 0.06, 0.3);
    const lid = new THREE.Mesh(lidGeo, redMat);
    lid.position.y = 0.25;
    lid.castShadow = true;

    // Metal carry handle
    const handleGeo = new THREE.TorusGeometry(0.07, 0.015, 4, 8, Math.PI);
    const handle = new THREE.Mesh(handleGeo, metalMat);
    handle.position.y = 0.29;
    handle.rotation.x = Math.PI / 2;

    // Latch
    const latchGeo = new THREE.BoxGeometry(0.06, 0.04, 0.03);
    const latch = new THREE.Mesh(latchGeo, metalMat);
    latch.position.set(0, 0.22, 0.16);

    group.add(body, lid, handle, latch);
    return group;
  },
  colliderSize: [0.5, 0.25, 0.3],
});

// ---------------------------------------------------------------------------
// Phase 3: Architectural Detail Props
// ---------------------------------------------------------------------------

// ---- Door (Wooden) ----
propRegistry.set('door_wooden', {
  generate: () => {
    const group = new THREE.Group();
    const woodMat = propMat(0x8b6f47, 0.85);
    const metalMat = propMat(0x777777, 0.3, 0.6);

    // Main panel
    const panelGeo = new THREE.BoxGeometry(1.0, 2.0, 0.06);
    const panel = new THREE.Mesh(panelGeo, woodMat);
    panel.position.y = 1.0;
    panel.castShadow = true;
    panel.receiveShadow = true;

    // Recessed panels (2 upper, 2 lower)
    const recessGeo = new THREE.BoxGeometry(0.35, 0.55, 0.02);
    const recessMat = propMat(0x7a5f3a, 0.9);
    const recessPositions = [
      [-0.18, 1.5], [0.18, 1.5], [-0.18, 0.6], [0.18, 0.6],
    ];
    for (const [x, y] of recessPositions) {
      const recess = new THREE.Mesh(recessGeo, recessMat);
      recess.position.set(x, y, 0.035);
      group.add(recess);
    }

    // Door knob
    const knobGeo = new THREE.SphereGeometry(0.03, 6, 6);
    const knob = new THREE.Mesh(knobGeo, metalMat);
    knob.position.set(0.38, 1.0, 0.05);

    // Hinges (2)
    const hingeGeo = new THREE.BoxGeometry(0.04, 0.1, 0.02);
    for (const y of [0.4, 1.6]) {
      const hinge = new THREE.Mesh(hingeGeo, metalMat);
      hinge.position.set(-0.48, y, 0.04);
      group.add(hinge);
    }

    group.add(panel, knob);
    return group;
  },
  colliderSize: [1.0, 2.0, 0.1],
});

// ---- Door (Metal) ----
propRegistry.set('door_metal', {
  generate: () => {
    const group = new THREE.Group();
    const metalMat = propMat(0x888888, 0.4, 0.5);
    const darkMat = propMat(0x555555, 0.3, 0.6);

    // Main panel
    const panelGeo = new THREE.BoxGeometry(1.0, 2.0, 0.06);
    const panel = new THREE.Mesh(panelGeo, metalMat);
    panel.position.y = 1.0;
    panel.castShadow = true;
    panel.receiveShadow = true;

    // Reinforcement ribs (3 horizontal)
    const ribGeo = new THREE.BoxGeometry(0.96, 0.04, 0.02);
    for (let i = 0; i < 3; i++) {
      const rib = new THREE.Mesh(ribGeo, darkMat);
      rib.position.set(0, 0.5 + i * 0.5, 0.04);
      group.add(rib);
    }

    // Handle bar
    const handleGeo = new THREE.BoxGeometry(0.04, 0.2, 0.06);
    const handle = new THREE.Mesh(handleGeo, darkMat);
    handle.position.set(0.38, 1.0, 0.06);

    // Kick plate at bottom
    const kickGeo = new THREE.BoxGeometry(0.96, 0.3, 0.01);
    const kick = new THREE.Mesh(kickGeo, darkMat);
    kick.position.set(0, 0.15, 0.035);

    group.add(panel, handle, kick);
    return group;
  },
  colliderSize: [1.0, 2.0, 0.1],
});

// ---- Window Frame ----
propRegistry.set('window_frame', {
  generate: () => {
    const group = new THREE.Group();
    const frameMat = propMat(0xa09070, 0.8);

    // Outer frame (4 pieces)
    const hGeo = new THREE.BoxGeometry(1.2, 0.08, 0.06);
    const vGeo = new THREE.BoxGeometry(0.08, 1.0, 0.06);

    const top = new THREE.Mesh(hGeo, frameMat);
    top.position.y = 0.5;
    top.castShadow = true;
    const bot = new THREE.Mesh(hGeo.clone(), frameMat);
    bot.position.y = -0.5;
    bot.castShadow = true;
    const left = new THREE.Mesh(vGeo, frameMat);
    left.position.x = -0.56;
    left.castShadow = true;
    const right = new THREE.Mesh(vGeo.clone(), frameMat);
    right.position.x = 0.56;
    right.castShadow = true;

    // Center cross divider
    const crossH = new THREE.Mesh(new THREE.BoxGeometry(1.04, 0.05, 0.06), frameMat);
    const crossV = new THREE.Mesh(new THREE.BoxGeometry(0.05, 0.92, 0.06), frameMat);

    group.add(top, bot, left, right, crossH, crossV);
    return group;
  },
  colliderSize: null,
});

// ---- Railing (Metal) ----
propRegistry.set('railing_metal', {
  generate: () => {
    const group = new THREE.Group();
    const metalMat = propMat(0x777777, 0.3, 0.6);

    // Top rail
    const railGeo = new THREE.CylinderGeometry(0.025, 0.025, 2.0, 6);
    railGeo.rotateZ(Math.PI / 2);
    const topRail = new THREE.Mesh(railGeo, metalMat);
    topRail.position.y = 1.0;
    topRail.castShadow = true;

    // Bottom rail
    const botRail = new THREE.Mesh(railGeo.clone(), metalMat);
    botRail.position.y = 0.1;

    // Vertical balusters (8 evenly spaced)
    const balGeo = new THREE.CylinderGeometry(0.015, 0.015, 0.9, 6);
    for (let i = 0; i < 8; i++) {
      const bal = new THREE.Mesh(balGeo, metalMat);
      bal.position.set(-0.875 + i * 0.25, 0.55, 0);
      bal.castShadow = true;
      group.add(bal);
    }

    // End posts (2)
    const postGeo = new THREE.CylinderGeometry(0.03, 0.03, 1.0, 6);
    for (const x of [-1.0, 1.0]) {
      const post = new THREE.Mesh(postGeo, metalMat);
      post.position.set(x, 0.5, 0);
      post.castShadow = true;
      group.add(post);
    }

    group.add(topRail, botRail);
    return group;
  },
  colliderSize: [2.0, 1.0, 0.05],
});

// ---- Vent (Wall-mounted) ----
propRegistry.set('vent_wall', {
  generate: () => {
    const group = new THREE.Group();
    const frameMat = propMat(0x888888, 0.5, 0.3);
    const slatMat = propMat(0x666666, 0.4, 0.4);

    // Outer frame
    const frameGeo = new THREE.BoxGeometry(0.6, 0.6, 0.04);
    const frame = new THREE.Mesh(frameGeo, frameMat);
    frame.castShadow = true;

    // Horizontal slats (6)
    const slatGeo = new THREE.BoxGeometry(0.52, 0.02, 0.03);
    for (let i = 0; i < 6; i++) {
      const slat = new THREE.Mesh(slatGeo, slatMat);
      slat.position.set(0, -0.2 + i * 0.08, 0.02);
      slat.rotation.x = 0.3;
      group.add(slat);
    }

    group.add(frame);
    return group;
  },
  colliderSize: null,
});

// ---- Vent (Floor) ----
propRegistry.set('vent_floor', {
  generate: () => {
    const group = new THREE.Group();
    const frameMat = propMat(0x777777, 0.5, 0.3);
    const barMat = propMat(0x555555, 0.4, 0.5);

    // Outer rim
    const rimGeo = new THREE.BoxGeometry(0.8, 0.02, 0.8);
    const rim = new THREE.Mesh(rimGeo, frameMat);
    rim.receiveShadow = true;

    // Grate bars (parallel)
    const barGeo = new THREE.BoxGeometry(0.72, 0.02, 0.03);
    for (let i = 0; i < 10; i++) {
      const bar = new THREE.Mesh(barGeo, barMat);
      bar.position.set(0, 0.01, -0.34 + i * 0.076);
      group.add(bar);
    }

    group.add(rim);
    return group;
  },
  colliderSize: null,
});

// ---- Gutter (Vertical Downspout) ----
propRegistry.set('gutter_vertical', {
  generate: () => {
    const group = new THREE.Group();
    const gutterMat = propMat(0x999999, 0.5, 0.3);

    // Main pipe
    const pipeGeo = new THREE.CylinderGeometry(0.05, 0.05, 3.0, 8);
    const pipe = new THREE.Mesh(pipeGeo, gutterMat);
    pipe.position.y = 1.5;
    pipe.castShadow = true;

    // Wall brackets (3)
    const bracketGeo = new THREE.BoxGeometry(0.1, 0.04, 0.08);
    for (const y of [0.5, 1.5, 2.5]) {
      const bracket = new THREE.Mesh(bracketGeo, gutterMat);
      bracket.position.set(0, y, -0.06);
      group.add(bracket);
    }

    // Elbow at bottom
    const elbowGeo = new THREE.CylinderGeometry(0.05, 0.05, 0.15, 8);
    const elbow = new THREE.Mesh(elbowGeo, gutterMat);
    elbow.position.set(0.06, 0.05, 0);
    elbow.rotation.z = Math.PI / 4;

    group.add(pipe, elbow);
    return group;
  },
  colliderSize: null,
});

// ---- Awning (Fabric) ----
propRegistry.set('awning_fabric', {
  generate: () => {
    const group = new THREE.Group();
    const fabricMat = propMat(0xcc6633, 0.9);
    const metalMat = propMat(0x777777, 0.3, 0.6);

    // Fabric canopy — slightly drooping shape via extruded arc
    const shape = new THREE.Shape();
    shape.moveTo(-1.5, 0);
    shape.quadraticCurveTo(0, -0.3, 1.5, 0);
    shape.lineTo(1.5, 0.04);
    shape.quadraticCurveTo(0, -0.26, -1.5, 0.04);
    shape.lineTo(-1.5, 0);

    const extrudeSettings: THREE.ExtrudeGeometryOptions = {
      steps: 1, depth: 2.0, bevelEnabled: false,
    };
    const canopyGeo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    canopyGeo.translate(0, 0, -1.0);
    const canopy = new THREE.Mesh(canopyGeo, fabricMat);
    canopy.castShadow = true;
    canopy.receiveShadow = true;

    // Support arms (2)
    const armGeo = new THREE.BoxGeometry(0.04, 0.04, 2.0);
    for (const x of [-1.3, 1.3]) {
      const arm = new THREE.Mesh(armGeo, metalMat);
      arm.position.set(x, -0.02, 0);
      arm.rotation.x = 0.15;
      group.add(arm);
    }

    group.add(canopy);
    return group;
  },
  colliderSize: null,
});

// ---- Concrete Bollard ----
propRegistry.set('concrete_bollard', {
  generate: () => {
    const group = new THREE.Group();
    const concreteMat = propMat(0xa09888, 0.85);

    // Main cylinder
    const bodyGeo = new THREE.CylinderGeometry(0.15, 0.17, 0.7, 10);
    const body = new THREE.Mesh(bodyGeo, concreteMat);
    body.position.y = 0.35;
    body.castShadow = true;
    body.receiveShadow = true;

    // Rounded top cap
    const capGeo = new THREE.SphereGeometry(0.15, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2);
    const cap = new THREE.Mesh(capGeo, concreteMat);
    cap.position.y = 0.7;
    cap.castShadow = true;

    // Reflective strip
    const stripGeo = new THREE.CylinderGeometry(0.16, 0.16, 0.06, 10);
    const stripMat = propMat(0xffcc00, 0.5);
    const strip = new THREE.Mesh(stripGeo, stripMat);
    strip.position.y = 0.6;

    group.add(body, cap, strip);
    return group;
  },
  colliderSize: [0.3, 0.8, 0.3],
});

// ---- Drain Cover ----
propRegistry.set('drain_cover', {
  generate: () => {
    const group = new THREE.Group();
    const metalMat = propMat(0x555555, 0.6, 0.3);

    // Circular rim
    const rimGeo = new THREE.CylinderGeometry(0.25, 0.25, 0.02, 16);
    const rim = new THREE.Mesh(rimGeo, metalMat);
    rim.position.y = 0.01;

    // Slotted grate pattern (parallel bars)
    const barGeo = new THREE.BoxGeometry(0.42, 0.02, 0.025);
    const barMat = propMat(0x444444, 0.5, 0.4);
    for (let i = 0; i < 7; i++) {
      const bar = new THREE.Mesh(barGeo, barMat);
      bar.position.set(0, 0.02, -0.15 + i * 0.05);
      group.add(bar);
    }

    group.add(rim);
    return group;
  },
  colliderSize: null,
});

// ---------------------------------------------------------------------------
// Phase 3: Environmental Detail Props
// ---------------------------------------------------------------------------

// ---- Rubble Pile ----
propRegistry.set('rubble_pile', {
  generate: () => {
    const group = new THREE.Group();
    const concreteMat = propMat(0x999988, 0.9);
    const darkMat = propMat(0x777766, 0.85);

    // Scattered concrete chunks at varying sizes/rotations
    const chunkGeo = new THREE.BoxGeometry(0.4, 0.25, 0.35);
    const positions = [
      [0, 0.12, 0], [0.4, 0.1, 0.3], [-0.35, 0.08, 0.2],
      [-0.3, 0.1, -0.35], [0.3, 0.06, -0.4], [0.1, 0.25, 0.1],
    ];
    for (let i = 0; i < positions.length; i++) {
      const [x, y, z] = positions[i];
      const mat = i % 2 === 0 ? concreteMat : darkMat;
      const chunk = new THREE.Mesh(chunkGeo, mat);
      chunk.position.set(x, y, z);
      chunk.rotation.set(
        (Math.random() - 0.5) * 0.4,
        (Math.random() - 0.5) * 0.8,
        (Math.random() - 0.5) * 0.3,
      );
      chunk.scale.set(0.6 + Math.random() * 0.8, 0.5 + Math.random() * 0.6, 0.6 + Math.random() * 0.8);
      chunk.castShadow = true;
      chunk.receiveShadow = true;
      group.add(chunk);
    }

    return group;
  },
  colliderSize: [1.5, 0.5, 1.5],
});

// ---- Potted Plant ----
propRegistry.set('potted_plant', {
  generate: () => {
    const group = new THREE.Group();
    const potMat = propMat(0x8b5e3c, 0.8);
    const soilMat = propMat(0x4a3520, 0.95);
    const leafMat = propMat(0x3a6a2a, 0.8);

    // Terracotta pot (tapered cylinder)
    const potGeo = new THREE.CylinderGeometry(0.18, 0.14, 0.35, 10);
    const pot = new THREE.Mesh(potGeo, potMat);
    pot.position.y = 0.175;
    pot.castShadow = true;
    pot.receiveShadow = true;

    // Pot rim
    const rimGeo = new THREE.TorusGeometry(0.19, 0.02, 4, 12);
    const rim = new THREE.Mesh(rimGeo, potMat);
    rim.rotation.x = Math.PI / 2;
    rim.position.y = 0.35;

    // Soil disc
    const soilGeo = new THREE.CylinderGeometry(0.16, 0.16, 0.04, 10);
    const soil = new THREE.Mesh(soilGeo, soilMat);
    soil.position.y = 0.34;

    // Plant foliage (cluster of spheres)
    const foliageGeo = new THREE.SphereGeometry(0.18, 8, 6);
    const foliagePositions = [
      [0, 0.6, 0], [0.1, 0.7, 0.08], [-0.08, 0.72, -0.06],
    ];
    for (const [x, y, z] of foliagePositions) {
      const leaf = new THREE.Mesh(foliageGeo, leafMat);
      leaf.position.set(x, y, z);
      leaf.scale.set(0.8 + Math.random() * 0.4, 0.8 + Math.random() * 0.4, 0.8 + Math.random() * 0.4);
      leaf.castShadow = true;
      group.add(leaf);
    }

    group.add(pot, rim, soil);
    return group;
  },
  colliderSize: [0.4, 0.9, 0.4],
});

// ---- Dead Bush ----
propRegistry.set('dead_bush', {
  generate: () => {
    const group = new THREE.Group();
    const branchMat = propMat(0x6a5030, 0.9);

    // Trunk stub
    const trunkGeo = new THREE.CylinderGeometry(0.03, 0.05, 0.3, 6);
    const trunk = new THREE.Mesh(trunkGeo, branchMat);
    trunk.position.y = 0.15;
    trunk.castShadow = true;

    // Branches radiating outward (6 angled sticks)
    const stickGeo = new THREE.CylinderGeometry(0.01, 0.02, 0.4, 4);
    for (let i = 0; i < 6; i++) {
      const stick = new THREE.Mesh(stickGeo, branchMat);
      const angle = (i / 6) * Math.PI * 2;
      stick.position.set(
        Math.cos(angle) * 0.15,
        0.3 + Math.random() * 0.15,
        Math.sin(angle) * 0.15,
      );
      stick.rotation.set(
        (Math.random() - 0.3) * 1.0,
        angle,
        (Math.random() - 0.5) * 0.6,
      );
      stick.castShadow = true;
      group.add(stick);
    }

    group.add(trunk);
    return group;
  },
  colliderSize: null,
});

// ---- Generator Unit ----
propRegistry.set('generator_unit', {
  generate: () => {
    const group = new THREE.Group();
    const bodyMat = propMat(0x556655, 0.6, 0.2);
    const metalMat = propMat(0x888888, 0.3, 0.6);
    const darkMat = propMat(0x333333, 0.7);

    // Main housing
    const housingGeo = new THREE.BoxGeometry(1.2, 0.7, 0.6);
    const housing = new THREE.Mesh(housingGeo, bodyMat);
    housing.position.y = 0.4;
    housing.castShadow = true;
    housing.receiveShadow = true;

    // Skid rails (2)
    const skidGeo = new THREE.BoxGeometry(1.3, 0.06, 0.08);
    for (const z of [-0.2, 0.2]) {
      const skid = new THREE.Mesh(skidGeo, metalMat);
      skid.position.set(0, 0.03, z);
      skid.castShadow = true;
      group.add(skid);
    }

    // Exhaust pipe
    const exhaustGeo = new THREE.CylinderGeometry(0.04, 0.04, 0.2, 6);
    const exhaust = new THREE.Mesh(exhaustGeo, darkMat);
    exhaust.position.set(0.5, 0.85, 0);

    // Control panel face
    const panelGeo = new THREE.BoxGeometry(0.3, 0.25, 0.01);
    const panelMat = propMat(0x444455, 0.5, 0.2);
    const panel = new THREE.Mesh(panelGeo, panelMat);
    panel.position.set(-0.2, 0.5, 0.31);

    // Fuel cap
    const capGeo = new THREE.CylinderGeometry(0.03, 0.03, 0.02, 8);
    const cap = new THREE.Mesh(capGeo, metalMat);
    cap.position.set(0.3, 0.76, 0);

    group.add(housing, exhaust, panel, cap);
    return group;
  },
  colliderSize: [1.2, 0.8, 0.6],
});

// ---- Satellite Dish ----
propRegistry.set('satellite_dish', {
  generate: () => {
    const group = new THREE.Group();
    const dishMat = propMat(0xcccccc, 0.5, 0.2);
    const metalMat = propMat(0x888888, 0.3, 0.6);

    // Mounting pole
    const poleGeo = new THREE.CylinderGeometry(0.03, 0.03, 0.5, 6);
    const pole = new THREE.Mesh(poleGeo, metalMat);
    pole.position.y = 0.25;
    pole.castShadow = true;

    // Dish (hemisphere-like — half sphere)
    const dishGeo = new THREE.SphereGeometry(0.4, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2);
    const dish = new THREE.Mesh(dishGeo, dishMat);
    dish.position.y = 0.55;
    dish.rotation.x = Math.PI * 0.65;
    dish.castShadow = true;

    // LNB arm + receiver
    const armGeo = new THREE.CylinderGeometry(0.01, 0.01, 0.35, 4);
    const arm = new THREE.Mesh(armGeo, metalMat);
    arm.position.set(0, 0.7, 0.2);
    arm.rotation.x = -0.5;

    const lnbGeo = new THREE.CylinderGeometry(0.025, 0.02, 0.06, 6);
    const lnb = new THREE.Mesh(lnbGeo, metalMat);
    lnb.position.set(0, 0.82, 0.34);

    group.add(pole, dish, arm, lnb);
    return group;
  },
  colliderSize: null,
});

// ---- Warning Sign ----
propRegistry.set('warning_sign', {
  generate: () => {
    const group = new THREE.Group();
    const poleMat = propMat(0x777777, 0.4, 0.5);
    const signMat = propMat(0xddcc00, 0.6);
    const stripeMat = propMat(0x222222, 0.7);

    // Pole
    const poleGeo = new THREE.CylinderGeometry(0.02, 0.02, 1.2, 6);
    const pole = new THREE.Mesh(poleGeo, poleMat);
    pole.position.y = 0.6;
    pole.castShadow = true;

    // Yellow diamond sign
    const signGeo = new THREE.BoxGeometry(0.35, 0.35, 0.02);
    const sign = new THREE.Mesh(signGeo, signMat);
    sign.position.y = 1.3;
    sign.rotation.z = Math.PI / 4;
    sign.castShadow = true;

    // Black border stripe (diagonal)
    const stripeGeo = new THREE.BoxGeometry(0.28, 0.04, 0.025);
    const stripe = new THREE.Mesh(stripeGeo, stripeMat);
    stripe.position.y = 1.3;
    stripe.rotation.z = Math.PI / 4;

    group.add(pole, sign, stripe);
    return group;
  },
  colliderSize: null,
});

// ---- Standing Lamp (Street Lamp) ----
propRegistry.set('standing_lamp', {
  generate: () => {
    const group = new THREE.Group();
    const poleMat = propMat(0x555555, 0.4, 0.5);

    // Base plate
    const baseGeo = new THREE.CylinderGeometry(0.12, 0.15, 0.08, 8);
    const base = new THREE.Mesh(baseGeo, poleMat);
    base.position.y = 0.04;
    base.castShadow = true;

    // Main pole
    const poleGeo = new THREE.CylinderGeometry(0.04, 0.06, 2.8, 8);
    const pole = new THREE.Mesh(poleGeo, poleMat);
    pole.position.y = 1.44;
    pole.castShadow = true;

    // Curved arm at top
    const armGeo = new THREE.CylinderGeometry(0.025, 0.025, 0.5, 6);
    const arm = new THREE.Mesh(armGeo, poleMat);
    arm.position.set(0.2, 2.85, 0);
    arm.rotation.z = Math.PI / 3;

    // Lamp head
    const headGeo = new THREE.CylinderGeometry(0.06, 0.15, 0.12, 8);
    const headMat = propMat(0x666666, 0.4, 0.3);
    const head = new THREE.Mesh(headGeo, headMat);
    head.position.set(0.35, 2.95, 0);

    // Glow lens (emissive)
    const lensGeo = new THREE.CylinderGeometry(0.12, 0.12, 0.02, 8);
    const lensMat = new THREE.MeshStandardMaterial({
      color: 0xffdd88, emissive: 0xffdd88, emissiveIntensity: 0.6,
    });
    const lens = new THREE.Mesh(lensGeo, lensMat);
    lens.position.set(0.35, 2.89, 0);

    group.add(base, pole, arm, head, lens);
    return group;
  },
  colliderSize: [0.15, 3.0, 0.15],
});

// ---- Emergency Light ----
propRegistry.set('emergency_light', {
  generate: () => {
    const group = new THREE.Group();
    const boxMat = propMat(0xcc2222, 0.5, 0.1);

    // Red housing box
    const housingGeo = new THREE.BoxGeometry(0.2, 0.12, 0.08);
    const housing = new THREE.Mesh(housingGeo, boxMat);

    // Lens (emissive glow)
    const lensGeo = new THREE.BoxGeometry(0.16, 0.08, 0.01);
    const lensMat = new THREE.MeshStandardMaterial({
      color: 0xff3333, emissive: 0xff3333, emissiveIntensity: 0.7,
    });
    const lens = new THREE.Mesh(lensGeo, lensMat);
    lens.position.z = 0.045;

    // Mounting plate
    const plateMat = propMat(0x444444, 0.5, 0.3);
    const plateGeo = new THREE.BoxGeometry(0.24, 0.04, 0.06);
    const plate = new THREE.Mesh(plateGeo, plateMat);
    plate.position.y = 0.08;

    group.add(housing, lens, plate);
    return group;
  },
  colliderSize: null,
});

// ---- Rebar Pile ----
propRegistry.set('rebar_pile', {
  generate: () => {
    const group = new THREE.Group();
    const rebarMat = propMat(0x8b5a2b, 0.6, 0.4);

    // Bundle of rebar rods (8 rods)
    const rodGeo = new THREE.CylinderGeometry(0.012, 0.012, 1.0, 4);
    rodGeo.rotateZ(Math.PI / 2);
    for (let i = 0; i < 8; i++) {
      const rod = new THREE.Mesh(rodGeo, rebarMat);
      const row = Math.floor(i / 4);
      const col = i % 4;
      rod.position.set(
        (Math.random() - 0.5) * 0.1,
        0.03 + row * 0.04,
        -0.1 + col * 0.07 + (Math.random() - 0.5) * 0.02,
      );
      rod.rotation.y = (Math.random() - 0.5) * 0.15;
      rod.castShadow = true;
      group.add(rod);
    }

    return group;
  },
  colliderSize: [1.0, 0.3, 0.5],
});

// ---- Wire Spool ----
propRegistry.set('wire_spool', {
  generate: () => {
    const group = new THREE.Group();
    const spoolMat = propMat(0x888888, 0.5, 0.3);
    const wireMat = propMat(0x444444, 0.6);

    // Core hub
    const hubGeo = new THREE.CylinderGeometry(0.06, 0.06, 0.18, 8);
    const hub = new THREE.Mesh(hubGeo, spoolMat);
    hub.position.y = 0.15;
    hub.rotation.x = Math.PI / 2;
    hub.castShadow = true;
    hub.receiveShadow = true;

    // Flanges (2 discs)
    const flangeGeo = new THREE.CylinderGeometry(0.15, 0.15, 0.02, 10);
    for (const z of [-0.1, 0.1]) {
      const flange = new THREE.Mesh(flangeGeo, spoolMat);
      flange.position.set(0, 0.15, z);
      flange.rotation.x = Math.PI / 2;
      flange.castShadow = true;
      flange.receiveShadow = true;
      group.add(flange);
    }

    // Wound wire layer
    const wireGeo = new THREE.TorusGeometry(0.1, 0.025, 6, 12);
    const wire = new THREE.Mesh(wireGeo, wireMat);
    wire.position.set(0, 0.15, 0);
    wire.rotation.x = Math.PI / 2;

    group.add(hub, wire);
    return group;
  },
  colliderSize: [0.3, 0.3, 0.2],
});

// ---------------------------------------------------------------------------
// Phase 4: Surface & Detail Props
// ---------------------------------------------------------------------------

// ── Phase 4: Surface & Detail Props ──

// ---- Wall Concrete Panel (4m) ----
propRegistry.set('wall_concrete_4m', {
  generate: () => {
    const group = new THREE.Group();
    const concreteMat = propMat(0xa09888, 0.9);
    const crackMat = propMat(0x807868, 0.95);

    // Main panel
    const panelGeo = new THREE.BoxGeometry(4.0, 3.0, 0.05);
    const panel = new THREE.Mesh(panelGeo, concreteMat);
    panel.position.y = 1.5;
    panel.castShadow = true;
    panel.receiveShadow = true;

    // Surface crack lines (3 thin strips)
    const crackGeo = new THREE.BoxGeometry(1.2, 0.01, 0.005);
    for (let i = 0; i < 3; i++) {
      const crack = new THREE.Mesh(crackGeo, crackMat);
      crack.position.set(-0.8 + i * 0.9, 0.8 + i * 0.5, 0.028);
      crack.rotation.z = (i - 1) * 0.2;
      group.add(crack);
    }

    // Stain patches (2 darker areas)
    const stainGeo = new THREE.BoxGeometry(0.6, 0.4, 0.002);
    const stainMat = propMat(0x8a7a6a, 0.95);
    const stain1 = new THREE.Mesh(stainGeo, stainMat);
    stain1.position.set(-1.0, 0.5, 0.027);
    const stain2 = new THREE.Mesh(stainGeo.clone(), stainMat);
    stain2.position.set(0.8, 2.2, 0.027);

    group.add(panel, stain1, stain2);
    return group;
  },
  colliderSize: null,
});

// ---- Wall Brick Panel (4m) ----
propRegistry.set('wall_brick_4m', {
  generate: () => {
    const group = new THREE.Group();
    const brickMat = propMat(0x8b4513, 0.85);
    const mortarMat = propMat(0xb0a090, 0.95);

    // Main panel body
    const panelGeo = new THREE.BoxGeometry(4.0, 3.0, 0.06);
    const panel = new THREE.Mesh(panelGeo, mortarMat);
    panel.position.y = 1.5;
    panel.castShadow = true;
    panel.receiveShadow = true;

    // Protruding brick rows (horizontal lines of bricks)
    const brickGeo = new THREE.BoxGeometry(0.22, 0.08, 0.025);
    for (let row = 0; row < 12; row++) {
      const offset = row % 2 === 0 ? 0 : 0.12;
      for (let col = 0; col < 6; col++) {
        const brick = new THREE.Mesh(brickGeo, brickMat);
        brick.position.set(
          -1.5 + offset + col * 0.65,
          0.2 + row * 0.24,
          0.04,
        );
        group.add(brick);
      }
    }

    group.add(panel);
    return group;
  },
  colliderSize: null,
});

// ---- Floor Tile Section (4m) ----
propRegistry.set('floor_tile_4m', {
  generate: () => {
    const group = new THREE.Group();
    const tileMat = propMat(0xc0b8a8, 0.8);
    const groutMat = propMat(0x908880, 0.9);

    // Base slab
    const baseGeo = new THREE.BoxGeometry(4.0, 0.02, 4.0);
    const base = new THREE.Mesh(baseGeo, groutMat);
    base.position.y = 0.01;
    base.receiveShadow = true;

    // Tile grid (4x4 tiles)
    const tileGeo = new THREE.BoxGeometry(0.92, 0.005, 0.92);
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 4; col++) {
        const tile = new THREE.Mesh(tileGeo, tileMat);
        tile.position.set(
          -1.5 + col * 1.0,
          0.023,
          -1.5 + row * 1.0,
        );
        tile.receiveShadow = true;
        group.add(tile);
      }
    }

    group.add(base);
    return group;
  },
  colliderSize: null,
});

// ---- Corner Concrete (L-shaped) ----
propRegistry.set('corner_concrete', {
  generate: () => {
    const group = new THREE.Group();
    const concreteMat = propMat(0xa09888, 0.85);

    // Two perpendicular wall segments forming an L
    const wallAGeo = new THREE.BoxGeometry(0.5, 3.0, 0.08);
    const wallA = new THREE.Mesh(wallAGeo, concreteMat);
    wallA.position.set(0.0, 1.5, -0.21);
    wallA.castShadow = true;
    wallA.receiveShadow = true;

    const wallBGeo = new THREE.BoxGeometry(0.08, 3.0, 0.5);
    const wallB = new THREE.Mesh(wallBGeo, concreteMat);
    wallB.position.set(-0.21, 1.5, 0.0);
    wallB.castShadow = true;
    wallB.receiveShadow = true;

    group.add(wallA, wallB);
    return group;
  },
  colliderSize: null,
});

// ---- Wall Damaged ----
propRegistry.set('wall_damaged', {
  generate: () => {
    const group = new THREE.Group();
    const concreteMat = propMat(0x999888, 0.9);
    const innerMat = propMat(0x6a5a4a, 0.85);
    const rebarMat = propMat(0x8b5a2b, 0.5, 0.5);

    // Main damaged panel
    const panelGeo = new THREE.BoxGeometry(3.0, 2.5, 0.08);
    const panel = new THREE.Mesh(panelGeo, concreteMat);
    panel.position.y = 1.25;
    panel.castShadow = true;
    panel.receiveShadow = true;

    // Hole / exposed inner face (recessed darker area)
    const holeGeo = new THREE.BoxGeometry(0.8, 0.6, 0.06);
    const hole = new THREE.Mesh(holeGeo, innerMat);
    hole.position.set(0.4, 1.1, -0.01);

    // Exposed rebar stubs (3 rods)
    const rodGeo = new THREE.CylinderGeometry(0.01, 0.01, 0.3, 4);
    rodGeo.rotateZ(Math.PI / 2);
    for (let i = 0; i < 3; i++) {
      const rod = new THREE.Mesh(rodGeo, rebarMat);
      rod.position.set(0.2 + i * 0.2, 1.0 + i * 0.15, 0.05);
      rod.rotation.y = (i - 1) * 0.15;
      rod.castShadow = true;
      group.add(rod);
    }

    // Crack lines
    const crackGeo = new THREE.BoxGeometry(0.6, 0.008, 0.005);
    const crackMat = propMat(0x706050, 0.95);
    for (let i = 0; i < 4; i++) {
      const crack = new THREE.Mesh(crackGeo, crackMat);
      crack.position.set(-0.5 + i * 0.6, 0.6 + i * 0.4, 0.042);
      crack.rotation.z = (i - 1.5) * 0.25;
      group.add(crack);
    }

    group.add(panel, hole);
    return group;
  },
  colliderSize: null,
});

// ---- Floor Metal (Diamond Plate) ----
propRegistry.set('floor_metal', {
  generate: () => {
    const group = new THREE.Group();
    const metalMat = propMat(0x888888, 0.4, 0.6);

    // Base plate
    const plateGeo = new THREE.BoxGeometry(2.0, 0.03, 2.0);
    const plate = new THREE.Mesh(plateGeo, metalMat);
    plate.position.y = 0.015;
    plate.receiveShadow = true;

    // Diamond ridges (diagonal lines)
    const ridgeGeo = new THREE.BoxGeometry(0.04, 0.008, 2.0);
    const ridgeMat = propMat(0x999999, 0.35, 0.7);
    for (let i = 0; i < 8; i++) {
      const ridge = new THREE.Mesh(ridgeGeo, ridgeMat);
      ridge.position.set(-0.8 + i * 0.24, 0.035, 0);
      ridge.rotation.y = Math.PI / 4;
      group.add(ridge);
    }

    group.add(plate);
    return group;
  },
  colliderSize: null,
});

// ---- Brick Protrusion ----
propRegistry.set('brick_protrusion', {
  generate: () => {
    const brickMat = propMat(0x8b4513, 0.85);
    const geo = new THREE.BoxGeometry(0.25, 0.12, 0.15);
    const mesh = new THREE.Mesh(geo, brickMat);
    mesh.castShadow = true;
    return mesh;
  },
  colliderSize: null,
});

// ---- Rebar Exposed ----
propRegistry.set('rebar_exposed', {
  generate: () => {
    const group = new THREE.Group();
    const rebarMat = propMat(0x8b5a2b, 0.5, 0.5);

    // 3 rebar rods sticking out at angles
    const rodGeo = new THREE.CylinderGeometry(0.01, 0.012, 0.3, 4);
    for (let i = 0; i < 3; i++) {
      const rod = new THREE.Mesh(rodGeo, rebarMat);
      rod.position.set(-0.2 + i * 0.2, 0.1 + i * 0.05, 0);
      rod.rotation.z = -0.4 + i * 0.3;
      rod.castShadow = true;
      group.add(rod);
    }

    // Base concrete chunk
    const chunkGeo = new THREE.BoxGeometry(0.8, 0.08, 0.05);
    const chunkMat = propMat(0x999888, 0.9);
    const chunk = new THREE.Mesh(chunkGeo, chunkMat);
    chunk.position.y = 0.02;
    chunk.castShadow = true;

    group.add(chunk);
    return group;
  },
  colliderSize: null,
});

// ---- Corrugated Panel ----
propRegistry.set('corrugated_panel', {
  generate: () => {
    const group = new THREE.Group();
    const metalMat = propMat(0x8a8a8a, 0.5, 0.4);

    // Base sheet
    const sheetGeo = new THREE.BoxGeometry(2.0, 1.5, 0.02);
    const sheet = new THREE.Mesh(sheetGeo, metalMat);
    sheet.position.y = 0.75;
    sheet.castShadow = true;
    sheet.receiveShadow = true;

    // Wavy ridges (horizontal corrugation)
    const ridgeGeo = new THREE.BoxGeometry(2.0, 0.04, 0.015);
    const ridgeMat = propMat(0x9a9a9a, 0.45, 0.5);
    for (let i = 0; i < 10; i++) {
      const ridge = new THREE.Mesh(ridgeGeo, ridgeMat);
      ridge.position.set(0, 0.1 + i * 0.14, 0.015);
      group.add(ridge);
    }

    group.add(sheet);
    return group;
  },
  colliderSize: null,
});

// ---- Curb Stone ----
propRegistry.set('curb_stone', {
  generate: () => {
    const concreteMat = propMat(0xb0a898, 0.85);
    const geo = new THREE.BoxGeometry(2.0, 0.15, 0.3);
    const mesh = new THREE.Mesh(geo, concreteMat);
    mesh.position.y = 0.075;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    return mesh;
  },
  colliderSize: [2.0, 0.15, 0.3],
});

// ---- Manhole Cover ----
propRegistry.set('manhole_cover', {
  generate: () => {
    const group = new THREE.Group();
    const metalMat = propMat(0x555555, 0.6, 0.3);

    // Circular base
    const discGeo = new THREE.CylinderGeometry(0.35, 0.35, 0.02, 16);
    const disc = new THREE.Mesh(discGeo, metalMat);
    disc.position.y = 0.01;

    // Cross-hatch pattern (2 perpendicular sets of bars)
    const barGeo = new THREE.BoxGeometry(0.58, 0.005, 0.03);
    const barMat = propMat(0x4a4a4a, 0.5, 0.4);
    for (let i = 0; i < 5; i++) {
      const bar = new THREE.Mesh(barGeo, barMat);
      bar.position.set(0, 0.022, -0.2 + i * 0.1);
      group.add(bar);
    }
    for (let i = 0; i < 5; i++) {
      const bar = new THREE.Mesh(barGeo.clone(), barMat);
      bar.position.set(-0.2 + i * 0.1, 0.022, 0);
      bar.rotation.y = Math.PI / 2;
      group.add(bar);
    }

    group.add(disc);
    return group;
  },
  colliderSize: null,
});

// ---- Pillar Concrete ----
propRegistry.set('pillar_concrete', {
  generate: () => {
    const group = new THREE.Group();
    const concreteMat = propMat(0xa09888, 0.85);
    const edgeMat = propMat(0x908878, 0.9);

    // Main column
    const colGeo = new THREE.BoxGeometry(0.4, 3.0, 0.4);
    const col = new THREE.Mesh(colGeo, concreteMat);
    col.position.y = 1.5;
    col.castShadow = true;
    col.receiveShadow = true;

    // Chamfered edges (4 thin strips)
    const chamferGeo = new THREE.BoxGeometry(0.04, 3.0, 0.04);
    const chamferPositions = [
      [-0.2, 1.5, -0.2], [0.2, 1.5, -0.2],
      [-0.2, 1.5, 0.2], [0.2, 1.5, 0.2],
    ];
    for (const [x, y, z] of chamferPositions) {
      const chamfer = new THREE.Mesh(chamferGeo, edgeMat);
      chamfer.position.set(x, y, z);
      chamfer.rotation.y = Math.PI / 4;
      group.add(chamfer);
    }

    // Base plinth (slightly wider)
    const plinthGeo = new THREE.BoxGeometry(0.48, 0.1, 0.48);
    const plinth = new THREE.Mesh(plinthGeo, concreteMat);
    plinth.position.y = 0.05;
    plinth.castShadow = true;
    plinth.receiveShadow = true;

    group.add(col, plinth);
    return group;
  },
  colliderSize: [0.4, 3.0, 0.4],
});

// ---- Door (Garage) ----
propRegistry.set('door_garage', {
  generate: () => {
    const group = new THREE.Group();
    const panelMat = propMat(0x808080, 0.5, 0.3);
    const trackMat = propMat(0x666666, 0.4, 0.5);

    // 5 horizontal panels with small gaps
    const panelGeo = new THREE.BoxGeometry(3.0, 0.45, 0.08);
    for (let i = 0; i < 5; i++) {
      const panel = new THREE.Mesh(panelGeo, panelMat);
      panel.position.y = 0.25 + i * 0.5;
      panel.castShadow = true;
      panel.receiveShadow = true;
      group.add(panel);
    }

    // Panel lines (grooves between panels)
    const lineGeo = new THREE.BoxGeometry(2.9, 0.02, 0.09);
    for (let i = 1; i < 5; i++) {
      const line = new THREE.Mesh(lineGeo, trackMat);
      line.position.y = i * 0.5;
      group.add(line);
    }

    // Tracks on both sides
    const trackGeo = new THREE.BoxGeometry(0.04, 2.5, 0.1);
    for (const x of [-1.52, 1.52]) {
      const track = new THREE.Mesh(trackGeo, trackMat);
      track.position.set(x, 1.25, 0);
      track.castShadow = true;
      group.add(track);
    }

    // Handle at bottom center
    const handleGeo = new THREE.BoxGeometry(0.15, 0.06, 0.04);
    const handle = new THREE.Mesh(handleGeo, trackMat);
    handle.position.set(0, 0.12, 0.06);
    group.add(handle);

    return group;
  },
  colliderSize: [3.0, 2.5, 0.08],
});

// ---- Railing (Concrete) ----
propRegistry.set('railing_concrete', {
  generate: () => {
    const group = new THREE.Group();
    const concreteMat = propMat(0xa0a090, 0.85);

    // Thick wall base
    const wallGeo = new THREE.BoxGeometry(2.0, 0.8, 0.12);
    const wall = new THREE.Mesh(wallGeo, concreteMat);
    wall.position.y = 0.4;
    wall.castShadow = true;
    wall.receiveShadow = true;

    // Wider top cap
    const capGeo = new THREE.BoxGeometry(2.0, 0.05, 0.18);
    const cap = new THREE.Mesh(capGeo, concreteMat);
    cap.position.y = 0.825;
    cap.castShadow = true;
    cap.receiveShadow = true;

    // 2 end posts
    const postGeo = new THREE.BoxGeometry(0.18, 1.0, 0.18);
    for (const x of [-0.91, 0.91]) {
      const post = new THREE.Mesh(postGeo, concreteMat);
      post.position.set(x, 0.5, 0);
      post.castShadow = true;
      post.receiveShadow = true;
      group.add(post);
    }

    group.add(wall, cap);
    return group;
  },
  colliderSize: [2.0, 1.0, 0.15],
});

// ---- Weeds ----
propRegistry.set('weeds', {
  generate: () => {
    const group = new THREE.Group();
    const bladeMat = propMat(0x6a8a4a, 0.8);

    // 6 thin blades angled outward
    const bladeGeo = new THREE.BoxGeometry(0.03, 0.25, 0.005);
    for (let i = 0; i < 6; i++) {
      const blade = new THREE.Mesh(bladeGeo, bladeMat);
      const angle = (i / 6) * Math.PI * 2;
      blade.position.set(
        Math.cos(angle) * 0.08,
        0.12,
        Math.sin(angle) * 0.08,
      );
      blade.rotation.set(
        (Math.random() - 0.3) * 0.6,
        angle,
        (Math.random() - 0.5) * 0.4,
      );
      blade.castShadow = true;
      group.add(blade);
    }

    return group;
  },
  colliderSize: null,
});

// ---------------------------------------------------------------------------
// Instanced prop rendering — now delegates to PropBatcher (BatchedMesh)
// ---------------------------------------------------------------------------

/**
 * After all props are placed, call this to convert repeated prop types
 * to BatchedMesh for draw-call reduction.
 *
 * @deprecated Use batchAllProps() for the new Phase 10 batching system.
 */
export function finalizeInstancing(scene: THREE.Scene): void {
  batchAllProps(scene);
}

/**
 * Batch all tracked props into BatchedMesh groups.
 * Call after loadProps() has placed all props in the scene.
 */
export function batchAllProps(scene: THREE.Scene): void {
  if (pendingBatchProps.length === 0) return;
  const batcher = getPropBatcher();
  batcher.batchProps(pendingBatchProps, scene);
  pendingBatchProps = [];
}

/** Props waiting to be batched after loadProps completes. */
let pendingBatchProps: BatchableProp[] = [];

// ---------------------------------------------------------------------------
// Blocking GLB prop load tracking — allows BVH rebuild after async loads
// ---------------------------------------------------------------------------

/** Number of blocking-priority GLB loads still in flight. */
let pendingBlockingLoads = 0;

/** Callback fired once all blocking GLB props have loaded (or failed). */
let blockingLoadCallback: (() => void) | null = null;

/**
 * Register a callback to run once all blocking-priority GLB props finish
 * loading. If no blocking loads are pending, fires immediately.
 * Used by scene.ts to rebuild the collision BVH after GLB meshes are in scene.
 */
export function onBlockingPropsLoaded(callback: () => void): void {
  if (pendingBlockingLoads <= 0) {
    callback();
    return;
  }
  blockingLoadCallback = callback;
}

/** Decrement pending count and fire callback when all blocking loads done. */
function resolveBlockingLoad(): void {
  pendingBlockingLoads--;
  if (pendingBlockingLoads <= 0 && blockingLoadCallback) {
    const cb = blockingLoadCallback;
    blockingLoadCallback = null;
    cb();
  }
}

// ---------------------------------------------------------------------------
// F-MAP-010: Dynamic GLB swap-in BVH rebuild
// ---------------------------------------------------------------------------

/**
 * Fired after each distance-based lazy GLB swap-in / swap-out so the host
 * can rebuild the collision BVH (otherwise bullets + capsule sweep keep
 * hitting the procedural cylinder/box silhouette while the player sees the
 * GLB shape).
 */
let propGLBSwapCallback: (() => void) | null = null;

/** How many GLB swap-ins / swap-outs happened since the last fired callback. */
let pendingPropGLBSwaps = 0;

/**
 * Register a callback that fires once-per-batch after dynamic GLB loads /
 * unloads complete. Coalesces N per-frame swaps into a single rebuild via the
 * `updatePropLODs` 30-frame throttle (see updatePropGLBs caller).
 *
 * scene.ts wires this to `buildCollisionBVH(scene)` + `setCollisionBVH(...)`.
 * Pass `null` to clear the registration (also done from `disposeProps`).
 */
export function onPropGLBSwapped(callback: (() => void) | null): void {
  propGLBSwapCallback = callback;
}

function flushPropGLBSwapBatch(): void {
  if (pendingPropGLBSwaps === 0) return;
  pendingPropGLBSwaps = 0;
  propGLBSwapCallback?.();
}

// ---------------------------------------------------------------------------
// LOD System — now delegates to PropLODSystem (quality-aware)
// ---------------------------------------------------------------------------

/** Frame counter for throttling GLB distance checks. */
let glbUpdateCounter = 0;

/** Update all LOD groups. Call each frame with the active camera. */
export function updatePropLODs(camera: THREE.Camera): void {
  const lodSystem = getPropLODSystem();
  lodSystem.update(camera);

  // Throttled GLB distance management (every 30 frames ~0.5s at 60fps)
  if (++glbUpdateCounter % 30 === 0) {
    updatePropGLBs(camera);
  }
}

/**
 * Update GLB prop loading based on camera distance.
 * - Props within GLB_LOAD_DISTANCE: trigger async GLB load if not loaded
 * - Props beyond GLB_UNLOAD_DISTANCE: unload GLB, revert to procedural
 *
 * Uses hysteresis (load at 50m, unload at 100m) to prevent thrashing at boundary.
 */
function updatePropGLBs(camera: THREE.Camera): void {
  if (managedProps.length === 0) return;

  const cameraPos = camera.position;
  const propModelLoader = getPropModelLoader();

  for (const mp of managedProps) {
    const dist = cameraPos.distanceTo(mp.position);

    if (dist < GLB_LOAD_DISTANCE && !mp.isGLBLoaded && !mp.isLoading) {
      // Within range — trigger GLB load
      if (propModelLoader.isOverBudget()) continue; // Skip if over memory budget

      mp.isLoading = true;
      propModelLoader.loadPropModel(mp.resolvedType).then((glbModel) => {
        mp.isLoading = false;
        if (!glbModel) return; // GLB not available, keep procedural

        // Hide procedural mesh
        mp.proceduralMesh.visible = false;

        // Position the GLB model
        const [x, y, z] = mp.propDef.position;
        glbModel.position.set(x, y, z);
        if (mp.propDef.rotation) {
          glbModel.rotation.set(mp.propDef.rotation[0], mp.propDef.rotation[1], mp.propDef.rotation[2]);
        }
        if (mp.propDef.scale) {
          glbModel.scale.set(mp.propDef.scale[0], mp.propDef.scale[1], mp.propDef.scale[2]);
        }

        mp.scene.add(glbModel);
        mp.glbModel = glbModel;
        mp.isGLBLoaded = true;
        // F-MAP-010: GLB shape often differs from the procedural fallback
        // (a real barrel ≠ procedural cylinder). Mark a swap so the host can
        // rebuild the collision BVH; otherwise bullets + capsule sweep keep
        // hitting the procedural silhouette.
        pendingPropGLBSwaps++;
      }).catch(() => {
        mp.isLoading = false;
        // Keep procedural mesh on failure
      });
    } else if (dist > GLB_UNLOAD_DISTANCE && mp.isGLBLoaded && mp.glbModel) {
      // Beyond range — unload GLB, restore procedural
      mp.scene.remove(mp.glbModel);

      // Dispose only cloned materials — geometry is shared with master copy
      // in PropModelLoader's cache and must NOT be disposed here
      mp.glbModel.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          // Do NOT dispose geometry — shared with master copy in PropModelLoader cache
          if (Array.isArray(child.material)) {
            child.material.forEach((m) => {
              if (m.map) { m.map = null; }
              if (m.normalMap) { m.normalMap = null; }
              if (m.roughnessMap) { m.roughnessMap = null; }
              m.dispose();
            });
          } else if (child.material) {
            if (child.material.map) { child.material.map = null; }
            if (child.material.normalMap) { child.material.normalMap = null; }
            if (child.material.roughnessMap) { child.material.roughnessMap = null; }
            child.material.dispose();
          }
        }
      });

      mp.glbModel = null;
      mp.isGLBLoaded = false;

      // Show procedural mesh again
      mp.proceduralMesh.visible = true;
      // F-MAP-010: see swap-in branch — symmetrically rebuild after unloads.
      pendingPropGLBSwaps++;
    }
  }

  // F-MAP-010: Coalesce all per-frame swaps into a single rebuild call so
  // updatePropLODs's 30-frame throttle drives at most one BVH rebuild per
  // batched window.
  flushPropGLBSwapBatch();
}

// ---------------------------------------------------------------------------
// Public API — build props from map data
// ---------------------------------------------------------------------------

/**
 * Place a single prop mesh (or group) into the scene with position/rotation/scale,
 * optional LOD wrapping, and collider generation.
 */
function placeProp(
  scene: THREE.Scene,
  mesh: THREE.Mesh | THREE.Group,
  propDef: PropDef,
  colliderSize: [number, number, number] | null,
  lodSystem: ReturnType<typeof getPropLODSystem>,
): void {
  const [x, y, z] = propDef.position;
  const shouldCollide = propDef.collide !== false;

  if (!shouldCollide) {
    markCollisionOptOut(mesh);
  }

  // Use LOD wrapper for props with collision (i.e., significant size)
  if (colliderSize && mesh instanceof THREE.Mesh) {
    const lod = lodSystem.createLOD(mesh, colliderSize);
    if (!shouldCollide) {
      markCollisionOptOut(lod);
    }
    lod.position.set(x, y, z);
    if (propDef.rotation) {
      lod.rotation.set(propDef.rotation[0], propDef.rotation[1], propDef.rotation[2]);
    }
    if (propDef.scale) {
      lod.scale.set(propDef.scale[0], propDef.scale[1], propDef.scale[2]);
    }
    scene.add(lod);
  } else {
    // Direct placement for Groups or small props
    if ('position' in mesh) {
      mesh.position.set(x, y, z);
    }
    if (propDef.rotation && 'rotation' in mesh) {
      mesh.rotation.set(propDef.rotation[0], propDef.rotation[1], propDef.rotation[2]);
    }
    if (propDef.scale && 'scale' in mesh) {
      mesh.scale.set(propDef.scale[0], propDef.scale[1], propDef.scale[2]);
    }
    scene.add(mesh);
  }

  // Add collider if prop has collision enabled
  if (shouldCollide && colliderSize) {
    const [cw, ch, cd] = colliderSize;
    const sx = propDef.scale?.[0] ?? 1;
    const sy = propDef.scale?.[1] ?? 1;
    const sz = propDef.scale?.[2] ?? 1;

    if (propDef.rotation && (propDef.rotation[0] !== 0 || propDef.rotation[1] !== 0 || propDef.rotation[2] !== 0)) {
      const tmpObj = new THREE.Object3D();
      tmpObj.position.set(x, y + (ch * sy) / 2, z);
      tmpObj.rotation.set(propDef.rotation[0], propDef.rotation[1], propDef.rotation[2]);
      tmpObj.updateMatrixWorld(true);
      const halfW = (cw * sx) / 2, halfH = (ch * sy) / 2, halfD = (cd * sz) / 2;
      const localBox = new THREE.Box3(
        new THREE.Vector3(-halfW, -halfH, -halfD),
        new THREE.Vector3(halfW, halfH, halfD),
      );
      localBox.applyMatrix4(tmpObj.matrixWorld);
      const center = new THREE.Vector3();
      const size = new THREE.Vector3();
      localBox.getCenter(center);
      localBox.getSize(size);
      addBoxCollider(center.x, center.y, center.z, size.x, size.y, size.z);
    } else {
      addBoxCollider(x, y + (ch * sy) / 2, z, cw * sx, ch * sy, cd * sz);
    }
  }
}

function markCollisionOptOut(root: THREE.Object3D): void {
  root.userData.cp_collision = false;
  root.traverse((child) => {
    child.userData.cp_collision = false;
  });
}

/**
 * Essential prop types that are always loaded, even on mobile-low.
 * These are gameplay-relevant (cover, obstacles) — decorative props are skipped.
 */
const ESSENTIAL_PROPS = new Set([
  'crate', 'barrel', 'car', 'barrier', 'sandbags', 'dumpster',
  'crate_small', 'barrel_rusty', 'ammo_crate', 'bench',
  'scaffolding', 'chain_fence', 'pillar_concrete',
  'standing_lamp', 'door_wooden', 'door_metal', 'ladder', 'railing_metal',
]);

/**
 * Build all props defined in the map data.
 *
 * First tries to load GLB models (same approach as weapon viewmodels).
 * Falls back to procedural mesh generators if GLB is unavailable.
 * Props with collision get wrapped in PropLODSystem. All props collected
 * for optional BatchedMesh batching — call batchAllProps(scene) after.
 *
 * Quality-aware filtering:
 * - mobile-low: skips ALL GLB loading + non-essential decorative props
 * - mobile-medium / low: skips lazy-priority GLB models
 */
export function loadProps(
  scene: THREE.Scene,
  props: PropDef[],
  matFactory: MaterialFactory,
): void {
  const quality = settingsStore.get().graphicsQuality;
  const lodSystem = getPropLODSystem(quality);
  const propModelLoader = getPropModelLoader();
  pendingBatchProps = [];
  pendingBlockingLoads = 0;
  blockingLoadCallback = null;

  // Determine which GLB priorities to load based on quality
  const skipGLB = quality === 'mobile-low'; // No GLBs at all on mobile-low
  const skipLazy = quality === 'mobile-medium' || quality === 'low'; // Skip lazy GLBs on low-end

  for (const propDef of props) {
    // F-MAP-009: resolve via the raw type first, then a sanitised fallback
    // (strip a trailing `_NNN` numeric suffix that Blender duplicates add).
    // This keeps already-correct names untouched while preventing silent
    // drops for typo-driven `crate_LARGE_001`-style markers.
    const rawType = propDef.type;
    const sanitisedType = sanitizePropType(rawType);
    const resolvedType = propRegistry.has(rawType) ? rawType : sanitisedType;

    // On mobile-low, skip non-essential decorative props entirely
    if (quality === 'mobile-low' && !ESSENTIAL_PROPS.has(resolvedType)) {
      continue;
    }

    const template = propRegistry.get(resolvedType);
    if (!template) {
      console.warn(`Unknown prop type: "${rawType}"`);
      continue;
    }

    const glbConfig = MAP_PROP_MODELS[resolvedType];
    const colliderSize = glbConfig?.colliderSize ?? template.colliderSize;

    // Try GLB model first (async — replaces procedural when loaded)
    // Skip GLB loading entirely on mobile-low, and skip lazy-priority GLBs on low-end
    if (glbConfig && !skipGLB && !(skipLazy && glbConfig.priority === 'lazy')) {
      // Place procedural mesh immediately (instant render)
      const proceduralMesh = template.generate(matFactory);
      placeProp(scene, proceduralMesh, propDef, colliderSize, lodSystem);

      // Collect for batching
      if (proceduralMesh instanceof THREE.Mesh && propDef.collide === false) {
        pendingBatchProps.push({
          mesh: proceduralMesh,
          propType: resolvedType,
          position: new THREE.Vector3(...propDef.position),
          rotation: propDef.rotation
            ? new THREE.Euler(propDef.rotation[0], propDef.rotation[1], propDef.rotation[2])
            : undefined,
          scale: propDef.scale
            ? new THREE.Vector3(propDef.scale[0], propDef.scale[1], propDef.scale[2])
            : undefined,
        });
      }

      // Track blocking-priority loads for BVH rebuild timing
      const isBlocking = glbConfig.priority === 'blocking';
      if (isBlocking) {
        pendingBlockingLoads++;
      }

      if (isBlocking) {
        // Blocking-priority props: load immediately (visible from spawn)
        propModelLoader.loadPropModel(resolvedType).then((glbModel) => {
          if (!glbModel) {
            resolveBlockingLoad();
            return;
          }

          // Dispose and remove the procedural mesh from scene
          if (proceduralMesh instanceof THREE.Mesh) {
            proceduralMesh.geometry?.dispose();
            if (Array.isArray(proceduralMesh.material)) {
              proceduralMesh.material.forEach((m) => m.dispose());
            } else {
              (proceduralMesh.material as THREE.Material)?.dispose();
            }
          }
          if (proceduralMesh.parent) {
            proceduralMesh.parent.remove(proceduralMesh);
          }

          // Place the GLB model in the same position.
          // Its child meshes will be included in the collision BVH rebuild
          // as regular scene meshes (no explicit cpCollision flag needed —
          // the fallback path in buildCollisionBVH picks up all visible meshes).
          placeProp(scene, glbModel, propDef, colliderSize, lodSystem);

          resolveBlockingLoad();
        }).catch(() => {
          resolveBlockingLoad();
        });
      } else {
        // Non-blocking props: register for distance-based lazy loading
        managedProps.push({
          propDef,
          resolvedType,
          proceduralMesh,
          glbModel: null,
          position: new THREE.Vector3(...propDef.position),
          isGLBLoaded: false,
          isLoading: false,
          glbConfig,
          scene,
          colliderSize,
        });
      }
    } else {
      // No GLB config — use procedural mesh only
      const mesh = template.generate(matFactory);
      placeProp(scene, mesh, propDef, colliderSize, lodSystem);

      if (mesh instanceof THREE.Mesh && propDef.collide === false) {
        pendingBatchProps.push({
          mesh,
          propType: resolvedType,
          position: new THREE.Vector3(...propDef.position),
          rotation: propDef.rotation
            ? new THREE.Euler(propDef.rotation[0], propDef.rotation[1], propDef.rotation[2])
            : undefined,
          scale: propDef.scale
            ? new THREE.Vector3(propDef.scale[0], propDef.scale[1], propDef.scale[2])
            : undefined,
        });
      }
    }
  }
}

/**
 * Get the prop registry for external use (e.g., editor tools).
 */
export function getPropTypes(): string[] {
  return Array.from(propRegistry.keys());
}

/**
 * Update prop LOD quality preset. Call when graphics settings change.
 */
export function setPropLODQuality(quality: import('../core/settings').GraphicsQuality): void {
  const lodSystem = getPropLODSystem();
  lodSystem.setQuality(quality);
}

/**
 * Dispose all prop optimization systems (batching + LOD).
 * Called from scene disposal.
 */
export function disposeProps(): void {
  disposePropBatcher();
  disposePropLODSystem();
  disposePropModelLoader();
  pendingBatchProps = [];
  pendingBlockingLoads = 0;
  blockingLoadCallback = null;
  // Clean up distance-based GLB management state
  managedProps.length = 0;
  glbUpdateCounter = 0;
  // F-MAP-010 — drop any registered swap-in BVH rebuild callback so a stale
  // closure doesn't fire after the next createScene() reseeds props.
  propGLBSwapCallback = null;
  pendingPropGLBSwaps = 0;
}
